/**
 * Centralized Module Configuration
 *
 * This file defines all available system modules, their navigation items,
 * and the relationship between modules and routes.
 *
 * Version: 1.0.0
 *
 * HOW TO ADD A NEW MODULE:
 * 1. Add the module ID to SystemModuleId enum in types/navigation.ts
 * 2. Add a new entry to MODULES_CONFIG below with:
 *    - id: The SystemModuleId enum value
 *    - title: Display name in the sidebar
 *    - icon: Lucide icon class (e.g., 'i-lucide-users')
 *    - basePath: Route prefix (e.g., '/customers')
 *    - order: Display order (lower numbers first)
 *    - items: Array of NavigationMenuItem for submenus (optional)
 *    - displayCondition: Function returning boolean (optional)
 *    - isFooter: true if it should appear in footer navigation (optional)
 * 3. The sidebar will automatically filter modules based on user permissions
 *
 * HOW MODULE-TO-ROUTE MAPPING WORKS:
 * - Each module has a basePath (e.g., '/sales')
 * - Routes starting with that path require access to that module
 * - The usePermissions composable can check if a user can access a route
 * - Module items (submenus) inherit the module's permission requirements
 */

import type { ModuleConfig, ModulesConfigMap } from '~/types/navigation'
import { SpecialModuleId, SystemModuleId } from '~/types/navigation'

/**
 * Centralized module configuration
 * All system modules are defined here with their navigation structure
 * Keys use the actual UUIDs from SystemModulesData (via SystemModuleId)
 */
export const MODULES_CONFIG: ModulesConfigMap = {
  [SystemModuleId.Customers]: {
    id: SystemModuleId.Customers,
    title: 'Clientes',
    titleKey: 'modules.customers.meta.plural',
    icon: 'i-lucide-users',
    basePath: '/customers',
    order: 2,
    items: [
      {
        icon: 'i-lucide-user-plus',
        label: 'Nuevo cliente',
        labelKey: 'modules.customers.navigation.new',
        to: '/customers/new',
        exact: true,
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-users',
        label: 'Listado de clientes',
        labelKey: 'modules.customers.navigation.list',
        to: '/customers',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
    ],
  },
  [SystemModuleId.Church]: {
    id: SystemModuleId.Church,
    title: 'Iglesia',
    icon: 'i-lucide-church',
    basePath: '/church',
    order: 8,
    items: [
      {
        icon: 'i-lucide-circle-plus',
        label: 'Nueva reunión',
        to: '/church/meetings/new',
        exact: true,
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-church',
        label: 'Listado de reuniones',
        to: '/church/meetings',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-settings',
        label: 'Configuraciones',
        to: '/church/settings',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
    ],
  },
  [SystemModuleId.Donations]: {
    id: SystemModuleId.Donations,
    title: 'Donaciones',
    icon: 'i-lucide-heart-handshake',
    basePath: '/donations',
    order: 9,
    items: [
      {
        icon: 'i-lucide-circle-plus',
        label: 'Nueva donación',
        to: '/donations/new',
        exact: true,
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-heart-handshake',
        label: 'Listado de donaciones',
        to: '/donations',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-settings',
        label: 'Configuraciones',
        to: '/donations/settings',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
    ],
  },
  [SystemModuleId.Services]: {
    id: SystemModuleId.Services,
    title: 'Servicios',
    icon: 'i-lucide-file-text',
    basePath: '/services',
    order: 3,
    items: [
      {
        icon: 'i-lucide-file-plus',
        label: 'Nuevo servicio',
        to: '/services/new',
        exact: true,
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-file-text',
        label: 'Listado de servicios',
        to: '/services',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
    ],
  },
  [SystemModuleId.Invoices]: {
    id: SystemModuleId.Invoices,
    title: 'Ventas',
    icon: 'i-lucide-circle-dollar-sign',
    basePath: '/sales',
    order: 4,
    items: [
      {
        icon: 'i-lucide-circle-plus',
        label: 'Nueva venta',
        to: '/sales/new',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-circle-dollar-sign',
        label: 'Listado de ventas',
        to: '/sales',
        exact: true,
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-settings',
        label: 'Configuraciones',
        to: '/sales/settings',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
    ],
  },
  [SystemModuleId.Accounting]: {
    id: SystemModuleId.Accounting,
    title: 'Contabilidad',
    icon: 'i-lucide-calculator',
    basePath: '/entries',
    order: 7,
    items: [
      {
        icon: 'i-lucide-circle-plus',
        label: 'Nueva partida',
        to: '/entries/new',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-calculator',
        label: 'Listado de partidas',
        to: '/entries',
        exact: true,
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-settings',
        label: 'Configuraciones',
        to: '/entries/settings',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
    ],
  },
  // Management module - appears in footer, not main nav
  // Note: Management is not a backend module, it's always available for authenticated users
  // This is a special module that doesn't follow the backend permission pattern
  [SpecialModuleId.Management]: {
    id: SpecialModuleId.Management,
    title: 'Administrar empresa',
    icon: 'i-lucide-building-2',
    basePath: '/management',
    order: 999,
    isFooter: true,
    isSpecial: true,
  },
  // Placeholder entries for modules not yet implemented in navigation
  [SystemModuleId.Providers]: {
    id: SystemModuleId.Providers,
    title: 'Proveedores',
    icon: 'i-lucide-truck',
    basePath: '/providers',
    order: 5,
    items: [
      {
        icon: 'i-lucide-user-plus',
        label: 'Nuevo proveedor',
        to: '/providers/new',
        exact: true,
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-truck',
        label: 'Listado de proveedores',
        to: '/providers',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
    ],
  },
  [SystemModuleId.Legal]: {
    id: SystemModuleId.Legal,
    title: 'Legal',
    icon: 'i-lucide-scale',
    basePath: '/legal',
    order: 12,
    items: [],
  },
  [SystemModuleId.Purchases]: {
    id: SystemModuleId.Purchases,
    title: 'Compras',
    icon: 'i-lucide-shopping-cart',
    basePath: '/purchases',
    order: 6,
    items: [
      {
        icon: 'i-lucide-circle-plus',
        label: 'Nueva compra',
        to: '/purchases/new',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-shopping-cart',
        label: 'Listado de compras',
        to: '/purchases',
        exact: true,
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
      {
        icon: 'i-lucide-settings',
        label: 'Configuraciones',
        to: '/purchases/settings',
        ui: {
          linkLeadingIcon: 'size-4',
        },
      },
    ],
  },
  [SystemModuleId.Echarges]: {
    id: SystemModuleId.Echarges,
    title: 'Cargos electrónicos',
    icon: 'i-lucide-zap',
    basePath: '/echarges',
    order: 13,
    items: [],
  },
  [SystemModuleId.Receivable]: {
    id: SystemModuleId.Receivable,
    title: 'Cuentas por cobrar',
    icon: 'i-lucide-credit-card',
    basePath: '/receivable',
    order: 14,
    items: [],
  },
  [SystemModuleId.Taxes]: {
    id: SystemModuleId.Taxes,
    title: 'Impuestos',
    icon: 'i-lucide-receipt',
    basePath: '/taxes',
    order: 15,
    items: [],
  },
  [SystemModuleId.Inventories]: {
    id: SystemModuleId.Inventories,
    title: 'Inventarios',
    icon: 'i-lucide-package',
    basePath: '/inventories',
    order: 16,
    items: [],
  },
  [SystemModuleId.Reports]: {
    id: SystemModuleId.Reports,
    title: 'Reportes',
    icon: 'i-lucide-chart-column-big',
    basePath: '/reports',
    order: 10,
    // Reports items will be dynamically calculated based on available modules
    // See getReportItemsForUserModules() in usePermissions composable
    items: [],
  },
  [SystemModuleId.FileCenter]: {
    id: SystemModuleId.FileCenter,
    title: 'Centro de archivos',
    icon: 'i-lucide-folder-open',
    basePath: '/file-center',
    order: 11,
  },
}

/**
 * Get module config by ID
 *
 * @param moduleId - The module ID to look up
 * @returns Module config or undefined if not found
 */
export function getModuleConfig(
  moduleId: SystemModuleId | SpecialModuleId | string
): ModuleConfig | undefined {
  return MODULES_CONFIG[moduleId]
}

/**
 * Get module config by route path
 * Matches the route against module basePaths
 *
 * @param routePath - The route path to match
 * @returns Module config or undefined if no match found
 */
export function getModuleConfigByRoute(
  routePath: string
): ModuleConfig | undefined {
  // Special case: root path (dashboard) is always accessible
  if (routePath === '/' || routePath === '') {
    return undefined
  }

  // Find the module whose basePath matches the route
  // We look for the longest matching basePath to handle nested paths
  let bestMatch: { config: ModuleConfig; length: number } | undefined

  for (const config of Object.values(MODULES_CONFIG)) {
    if (routePath.startsWith(config.basePath)) {
      const matchLength = config.basePath.length
      if (!bestMatch || matchLength > bestMatch.length) {
        bestMatch = { config, length: matchLength }
      }
    }
  }

  return bestMatch?.config
}

/**
 * Get all module configs sorted by order
 *
 * @param includeFooter - Whether to include footer modules (default: false)
 * @returns Array of module configs sorted by order
 */
export function getAllModuleConfigs(includeFooter = false): ModuleConfig[] {
  return Object.values(MODULES_CONFIG)
    .filter(config => (includeFooter ? true : !config.isFooter))
    .sort((a, b) => a.order - b.order)
}

/**
 * Get footer module configs sorted by order
 *
 * @returns Array of footer module configs sorted by order
 */
export function getFooterModuleConfigs(): ModuleConfig[] {
  return Object.values(MODULES_CONFIG)
    .filter(config => config.isFooter)
    .sort((a, b) => a.order - b.order)
}
